/** 화면용. 저장 형식의 기준은 백엔드 ruleParams 정규화와 같다. */

const MAX_CONFIRM_MINUTES = 10080;
const MAX_MESSAGE_KEYS = 50;
const MAX_MESSAGE_LEN = 256;

function parseRuleParams(raw) {
    if (raw == null || raw === "") return {};
    let value = raw;
    if (typeof value === "string") {
        try {
            value = JSON.parse(value);
        } catch (e) {
            return {};
        }
    }
    if (!value || typeof value !== "object" || Array.isArray(value)) return {};
    return value;
}

export function confirmUiFromRuleParams(raw) {
    const confirm = parseRuleParams(raw).confirm;
    if (confirm && confirm.mode === "sustain") {
        const minutes = Math.floor(Number(confirm.minutes));
        return {
            mode: "sustain",
            minutes: Number.isFinite(minutes) && minutes > 0 ? minutes : 10,
        };
    }
    return { mode: "immediate", minutes: 10 };
}

/** 저장된 messages 맵을 화면의 행 목록으로 편다. */
export function messageRowsFromRuleParams(raw) {
    const messages = parseRuleParams(raw).messages;
    if (!messages || typeof messages !== "object" || Array.isArray(messages)) return [];
    return Object.entries(messages).map(([key, text]) => ({ key: String(key), text: String(text ?? "") }));
}

function buildConfirm(ui) {
    if (ui.mode !== "sustain") return { ok: true, value: null };
    const parsed = Number(ui.minutes);
    const whole = Math.floor(parsed);
    if (!Number.isFinite(parsed) || whole !== parsed || whole < 1 || whole > MAX_CONFIRM_MINUTES) {
        return {
            ok: false,
            message: `지속 시간은 1분 이상 ${MAX_CONFIRM_MINUTES}분 이하의 정수로 입력해 주세요.`,
        };
    }
    return { ok: true, value: { mode: "sustain", minutes: whole } };
}

/** 행 목록을 맵으로 접는다. 키와 문구가 모두 빈 행은 버린다. */
function buildMessages(rows) {
    const list = Array.isArray(rows) ? rows : [];
    const messages = {};

    for (const row of list) {
        const key = String(row?.key ?? "").trim();
        const text = String(row?.text ?? "").trim();
        if (!key && !text) continue;
        if (!key) return { ok: false, message: "알람 문구를 적은 행에는 키를 입력해 주세요." };
        if (!text) continue;
        if (text.length > MAX_MESSAGE_LEN) {
            return { ok: false, message: `알람 문구는 ${MAX_MESSAGE_LEN}자 이하로 입력해 주세요.` };
        }
        if (messages[key] !== undefined) {
            return { ok: false, message: `키 '${key}' 가 중복됐습니다.` };
        }
        messages[key] = text;
    }

    const keys = Object.keys(messages);
    if (!keys.length) return { ok: true, value: null };
    if (keys.length > MAX_MESSAGE_KEYS) {
        return { ok: false, message: `알람 문구는 최대 ${MAX_MESSAGE_KEYS}개까지 설정할 수 있습니다.` };
    }
    return { ok: true, value: messages };
}

/**
 * 판정 방식별 ruleParams 생성.
 * @param {object} ui { mode, minutes, messageRows }
 */
export function buildRuleParams(alarmType, ui = {}) {
    const params = {};

    if (alarmType === "D0" || alarmType === "D1") {
        const confirm = buildConfirm(ui);
        if (!confirm.ok) return confirm;
        if (confirm.value) params.confirm = confirm.value;
    }

    const messages = buildMessages(ui.messageRows);
    if (!messages.ok) return messages;
    if (messages.value) params.messages = messages.value;

    return { ok: true, params: Object.keys(params).length ? params : null };
}

export function formatD0Criteria(hh, ruleParams) {
    const mark = Number(hh) === 0 ? "F" : "T";
    const ui = confirmUiFromRuleParams(ruleParams);
    if (ui.mode === "sustain") return `${mark}·${ui.minutes}분`;
    return mark;
}

export function formatChangeCriteria(ruleParams) {
    const ui = confirmUiFromRuleParams(ruleParams);
    if (ui.mode === "sustain") return `변경·${ui.minutes}분`;
    return "변경";
}

/** 상세 화면의 한 줄 요약. */
export function describeChangeRule(ui) {
    if (ui.mode === "sustain") {
        const minutes = Math.floor(Number(ui.minutes));
        if (Number.isFinite(minutes) && minutes > 0) {
            return `값이 바뀌고 ${minutes}분 유지되면 알람이 발생합니다.`;
        }
    }
    return "값이 바뀌면 알람이 발생합니다.";
}
